import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';

export const FONT_SIZES = [
  { id: 'small',   label: 'Small',    scale: 0.9 },
  { id: 'default', label: 'Default',  scale: 1.0 },
  { id: 'large',   label: 'Large',    scale: 1.15 },
  { id: 'larger',  label: 'Larger',   scale: 1.3 },
  { id: 'largest', label: 'Largest',  scale: 1.5 },
];

export const FONT_FAMILIES = [
  { id: 'system',     label: 'System Default', stack: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif" },
  { id: 'comic',      label: 'Comic Sans (Friendly)', stack: "'Comic Sans MS', 'Chalkboard SE', 'Comic Neue', sans-serif" },
  { id: 'verdana',    label: 'Verdana (Easy Reading)', stack: "Verdana, Geneva, 'DejaVu Sans', sans-serif" },
  { id: 'georgia',    label: 'Georgia (Serif)', stack: "Georgia, 'Times New Roman', serif" },
  { id: 'helvetica',  label: 'Helvetica / Arial', stack: "'Helvetica Neue', Helvetica, Arial, sans-serif" },
];

const STORAGE_KEY = 'literacy-app:displaySettings:v1';

const DEFAULTS = { fontSizeId: 'default', fontFamilyId: 'comic' };

const DisplaySettingsContext = createContext({
  fontSizeId: DEFAULTS.fontSizeId,
  fontFamilyId: DEFAULTS.fontFamilyId,
  setFontSize: () => {},
  setFontFamily: () => {},
  resetToDefaults: () => {},
});

const readStorage = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    const parsed = JSON.parse(raw);
    return {
      fontSizeId: FONT_SIZES.some(s => s.id === parsed.fontSizeId) ? parsed.fontSizeId : DEFAULTS.fontSizeId,
      fontFamilyId: FONT_FAMILIES.some(f => f.id === parsed.fontFamilyId) ? parsed.fontFamilyId : DEFAULTS.fontFamilyId,
    };
  } catch {
    return DEFAULTS;
  }
};

const writeStorage = (settings) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    /* ignore quota / privacy mode errors */
  }
};

const applyToDocument = (fontSizeId, fontFamilyId) => {
  const size = FONT_SIZES.find(s => s.id === fontSizeId) || FONT_SIZES[1];
  const family = FONT_FAMILIES.find(f => f.id === fontFamilyId) || FONT_FAMILIES[1];
  const root = document.documentElement;
  root.style.setProperty('--app-font-scale', String(size.scale));
  root.style.setProperty('--app-font-family', family.stack);
};

export function DisplaySettingsProvider({ children }) {
  const [{ fontSizeId, fontFamilyId }, setSettings] = useState(() => readStorage());

  useEffect(() => {
    applyToDocument(fontSizeId, fontFamilyId);
    writeStorage({ fontSizeId, fontFamilyId });
  }, [fontSizeId, fontFamilyId]);

  const setFontSize = useCallback((id) => {
    setSettings(prev => ({ ...prev, fontSizeId: id }));
  }, []);

  const setFontFamily = useCallback((id) => {
    setSettings(prev => ({ ...prev, fontFamilyId: id }));
  }, []);

  const resetToDefaults = useCallback(() => {
    setSettings(DEFAULTS);
  }, []);

  const value = useMemo(() => ({
    fontSizeId, fontFamilyId, setFontSize, setFontFamily, resetToDefaults,
  }), [fontSizeId, fontFamilyId, setFontSize, setFontFamily, resetToDefaults]);

  return (
    <DisplaySettingsContext.Provider value={value}>
      {children}
    </DisplaySettingsContext.Provider>
  );
}

export function useDisplaySettings() {
  return useContext(DisplaySettingsContext);
}
